<script setup lang="ts">import { computed as _computed, ref as _ref } from 'vue';

import type GiftTemplate from '@consumer/types/GiftTemplate'
import type { FeaturedGift, GiftlyDesign } from '@consumer/types/serializers'
import GButton from '@consumer/components/GButton.vue'
import GiftlyV3Image from '@consumer/components/GiftlyV3Image.vue'
import AmountButton from '@consumer/modules/multiPlaceGiftIdea/AmountButton.vue'
import CustomAmountForm from '@consumer/modules/multiPlaceGiftIdea/CustomAmountForm.vue'
import DesignButton from '@consumer/modules/multiPlaceGiftIdea/DesignButton.vue'
import { checkoutGift, useOrderStore } from '@consumer/services/order'
import { cloneDeep } from '@shared/object'
import { formatCurrency } from '@consumer/utils/currency'
import { applyDesign } from '@consumer/utils/design'
import { v3WithImageBg } from '@consumer/logic/v3'
import { faXmark } from '@fortawesome/pro-regular-svg-icons'
import newPlusIcon from '@consumer/assets/icons/new/plus.svg?svgicon'

withDefaults(defineProps<FeaturedGift>(), {  })

onMounted(() => useOrderStore().initialize())

const localGift = reactive<GiftTemplate>({
  amount: __props.defaultAmount,
  places: cloneDeep(__props.places),
  giftlyMetadata: cloneDeep(__props.giftlyMetadata),
  metaPixelContentId: __props.metaPixelContentId,
  giftlyVersion: 3,
})

// A multi-place gift with a background image (or no named design) carries its
// own custom design, offered as the first swatch — mirrors MultiPlaceGiftIdea.
const customDesign = _computed(() => {
  const designName = __props.giftlyMetadata.designName
  if (!designName || designName === v3WithImageBg) {
    return {
      ...__props.giftlyMetadata,
      id: 'custom-design',
      imageUrl: __props.giftlyMetadata.backgroundImageUrl ?? '',
      provider: 'giftly',
      custom: true,
    } satisfies GiftlyDesign
  }
})

const allDesigns = _computed(() => customDesign.value ? [customDesign.value, ...__props.giftlyDesigns] : __props.giftlyDesigns)

let showAllDesigns = _ref(false)
const displayedDesigns = _computed(() => allDesigns.value.slice(0, showAllDesigns.value ? 20 : 4))

let selectedDesign = _ref<GiftlyDesign>(findGiftlyDesign(__props.initialDesignId))

function findGiftlyDesign (id?: string) {
  return (id && allDesigns.value.find(design => design.id === id)) || allDesigns.value[0]
}

function selectDesign (design: GiftlyDesign) {
  selectedDesign.value = design
}

function revealAllDesigns () {
  showAllDesigns.value = true
}

watch(() => selectedDesign.value, (design) => {
  applyDesign(localGift.giftlyMetadata ||= {}, design)
}, { immediate: true })

const themeColor = _computed(() => selectedDesign.value.themeColor || selectedDesign.value.maskColor || '#3D2B1F')

const minimumAmount = 5
const maximumAmount = 1000

let selectedAmount = _ref(__props.defaultAmount || __props.amounts[0])
let customAmount = _ref<number | false>(!__props.amounts.includes(selectedAmount.value) && selectedAmount.value)
let customAmountModalOpen = _ref(false)
const customAmountFormRef = _ref<InstanceType<typeof CustomAmountForm>>()

function selectAmount (amount: number) {
  selectedAmount.value = amount
  customAmount.value = !__props.amounts.includes(amount) && amount
  customAmountModalOpen.value = false
}

function selectCustomAmount () {
  customAmountModalOpen.value = true
  nextTick(() => {
    customAmountFormRef.value?.focus()
  })
}

function buyNow () {
  checkoutGift(
    { ...localGift, amount: selectedAmount.value },
    { specOptions: { purchaseMethod: __props.purchaseMethod } },
  )
}

const fullDetailsUrl = _computed(() => {
  const query = new URLSearchParams({ amount: String(selectedAmount.value) })
  if (!selectedDesign.value.custom) query.set('giftly_design_name', selectedDesign.value.id)
  return `${__props.productUrl}${__props.productUrl.includes('?') ? '&' : '?'}${query}`
})

const descriptionEl = _ref<HTMLElement>()
let descriptionExpanded = _ref(false)
let descriptionTruncated = _ref(false)

onMounted(() => {
  descriptionTruncated.value = Boolean(descriptionEl.value
    && descriptionEl.value.scrollHeight > descriptionEl.value.clientHeight)
})

function toggleDescription () {
  descriptionExpanded.value = !descriptionExpanded.value
}
</script>

<template>
  <section
    class="featured-gift-section relative w-full rounded-v2lg overflow-hidden px-6 py-8 lg:px-14 lg:py-12"
    :style="{ backgroundColor: themeColor, transition: 'background-color 0.2s ease 0s' }"
    data-testid="featured-gift-section"
  >
    <!-- Closing restores the landing page's own hero: the same URL without the
    feature params renders the hero and skips this section. -->
    <a
      :href="closeUrl"
      class="absolute right-4 top-4 z-1 text-white p-2 -m-2"
      aria-label="Close featured gift"
      data-testid="close-featured-gift-button"
    >
      <GIcon :icon="faXmark" size="24px"/>
    </a>

    <div class="flex flex-col lg:flex-row gap-8 lg:gap-14 items-center lg:items-start justify-center">
      <div class="w-full max-w-md lg:max-w-470px flex flex-col gap-5">
        <GiftlyV3Image :gift="localGift" :inCustomizer="true" class="w-full"/>

        <div v-if="cardLabel || description" class="flex flex-col gap-2 items-start">
          <p v-if="cardLabel" class="text-white text-lg font-semibold">
            {{ cardLabel }}
          </p>
          <p
            v-if="description"
            ref="descriptionEl"
            class="text-white text-base opacity-90 whitespace-pre-line"
            :class="{ 'featured-gift-section__description': !descriptionExpanded }"
          >
            {{ description }}
          </p>
          <button
            v-if="descriptionTruncated"
            class="text-white text-sm font-medium underline opacity-90 hover:opacity-100"
            data-testid="featured-gift-read-more-button"
            @click="toggleDescription"
          >
            {{ descriptionExpanded ? 'Read less' : 'Read more…' }}
          </button>
        </div>
      </div>

      <div
        class="w-full max-w-md lg:w-400px flex-shrink-0 bg-white rounded-v2lg px-6 py-6 lg:px-8 flex flex-col gap-4"
        :style="{ boxShadow: '0px 0px 15px 0px rgba(0, 0, 0, 0.15)' }"
      >
        <h2 class="text-xl text-navy font-semibold">Customize gift</h2>

        <div class="grid grid-cols-5 gap-y-1">
          <DesignButton
            v-for="design in displayedDesigns"
            :key="design.id"
            :design="design"
            :isSelected="selectedDesign?.id === design.id"
            @click="selectDesign(design)"
          />
          <DesignButton
            v-if="!showAllDesigns && allDesigns.length > 4"
            aria-label="Show more designs"
            @click="revealAllDesigns"
          >
            <i
              class="rounded-full w-full h-full flex items-center justify-center
              bg-grey-200 border-2 border-solid border-white"
            >
              <GIcon :svg="newPlusIcon" size="40px" class="text-grey-500 hover:text-grey-800"/>
            </i>
          </DesignButton>
        </div>

        <div class="grid grid-cols-4 gap-2">
          <AmountButton
            v-for="amount in amounts.slice(0, 3)"
            :key="amount"
            :amount="amount"
            :selectedAmount="selectedAmount"
            @selectAmount="selectAmount"
          />
          <GButton
            name="other-amount-button"
            size="small"
            secondary
            class="w-full !text-lg transition-all ease-in-out duration-200 !rounded-v2sm
            hover:border-2 !hover:border-primary-base !hover:bg-primary-pale border-solid"
            :class="[
              selectedAmount === customAmount ?
                '!border-primary-base !bg-primary-pale' :
                '!bg-grey-200 !text-grey-500 !border-grey-400',
            ]"
            @click="selectCustomAmount"
          >
            {{ customAmount ? formatCurrency(customAmount) : 'Other' }}
          </GButton>
        </div>

        <GButton
          name="featured-buy-now"
          size="large"
          primary
          class="w-full"
          data-testid="featured-buy-now-button"
          @click="buyNow"
        >
          Buy Now
        </GButton>
        <GButton
          name="featured-view-full-details"
          size="large"
          secondary
          class="w-full"
          :href="fullDetailsUrl"
          :aria-label="`View full details for ${giftName}`"
          data-testid="featured-view-full-details-button"
        >
          View full details
        </GButton>
      </div>
    </div>

    <GModal
      v-model:open="customAmountModalOpen"
      :fullscreenMobile="true"
      name="custom-amount-modal"
      class="w-full lg:w-md"
      @close="customAmountModalOpen = false"
    >
      <CustomAmountForm
        ref="customAmountFormRef"
        :minimumAmount="minimumAmount"
        :maximumAmount="maximumAmount"
        @save="selectAmount"
      />
    </GModal>
  </section>
</template>

<style lang="scss" scoped>
.featured-gift-section__description {
  -webkit-box-orient: vertical;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  overflow: hidden;
}
</style>
